import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import {
  UtensilsCrossed,
  RefreshCw,
  Clock,
  Building2,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  useMenus,
  MenuSuggestionModal,
} from '@/features/menus';
import type {
  Menu,
  MenuStatus,
  MenuType,
  MenuSuggestion,
} from '@/features/menus';
import { useSites } from '@/features/settings';
import {
  useRecipes, RecipeDetailModal 
} from '@/features/recipes';
import type { Recipe } from '@/features/recipes';
import { toast } from 'sonner';
import {
  TABS, MEAL_TYPES 
} from './constants';
import {
  getDaysInMonth, getMenusForDate 
} from './calendar-operations';
import { CalendarView } from './calendar-view';
import { ForecastView } from './forecast-view';
import { OverviewView } from './overview-view';

interface MenuFormData {
  name: string;
  venueId: string;
  type: MenuType;
  description: string;
  effectiveFrom: string;
  effectiveTo: string;
  defaultPax: string;
}

const INITIAL_FORM_DATA: MenuFormData = {
  name: '',
  venueId: '',
  type: 'lunch',
  description: '',
  effectiveFrom: '',
  effectiveTo: '',
  defaultPax: '',
};

export function MenusPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Filters
  const [selectedSiteId, setSelectedSiteId] = useState('site-celebration');
  const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<MenuStatus | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<MenuType | undefined>(undefined);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 0, 1));
  const [forecastPax, setForecastPax] = useState<Record<string, number>>({});
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [editingPax, setEditingPax] = useState<Record<string, number>>({});

  // CRUD state
  const [selectedMenus, setSelectedMenus] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAISuggestionModal, setShowAISuggestionModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState<string | null>(null);
  const [menuFormData, setMenuFormData] = useState<MenuFormData>(INITIAL_FORM_DATA);
  const [viewingMenu, setViewingMenu] = useState<Menu | null>(null);
  const [viewingRecipe, setViewingRecipe] = useState<Recipe | null>(null);

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/calendar')) return 'calendar';
    if (location.pathname.includes('/forecast')) return 'forecast';
    return 'overview';
  }, [location.pathname]);

  // Fetch sites for filtering
  const { data: sitesData } = useSites();
  const sites = sitesData?.data ?? [];
  const selectedSite = sites.find((s) => s.siteId === selectedSiteId);
  const venues = selectedSite?.venues ?? [];

  // Fetch menus
  const {
    data, isLoading, error, refetch, isFetching 
  } = useMenus(selectedSiteId, {
    venueId: selectedVenueId,
    status: selectedStatus,
    type: selectedType,
  });
  const { data: allData } = useMenus(selectedSiteId);

  const menus = data?.data ?? [];

  // Fetch recipes for menu item selection
  const { data: recipesData } = useRecipes({ filters: { type: 'finished' } });
  const availableRecipes = recipesData?.data ?? [];

  const stats = useMemo(() => {
    const ms = allData?.data ?? [];
    const totalItems = ms.reduce((sum, m) => sum + m.totalItems, 0);
    const totalPax = ms.reduce((sum, m) => sum + (m.defaultPax ?? 0), 0);
    const totalCapacity = ms.reduce((sum, m) => sum + (m.venueCapacity ?? 0), 0);
    return {
      total: ms.length,
      active: ms.filter((m) => m.status === 'active').length,
      draft: ms.filter((m) => m.status === 'draft').length,
      totalItems,
      totalPax,
      totalCapacity,
      utilization: totalCapacity > 0 ? Math.round((totalPax / totalCapacity) * 100) : 0,
    };
  }, [allData]);

  const calendarDays = getDaysInMonth(currentMonth);

  // Calendar handlers
  const openDayModal = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const menusForDay = getMenusForDate(menus, date);
    const initialPax: Record<string, number> = {};
    for (const menu of menusForDay) {
      const schedule = menu.schedule?.find((s) => s.date === dateStr);
      initialPax[menu.menuId] = forecastPax[`${menu.menuId}-${dateStr}`] ?? schedule?.expectedPax ?? menu.defaultPax ?? 0;
    }
    setEditingPax(initialPax);
    setSelectedDate(date);
  };

  const saveDayForecast = () => {
    if (!selectedDate) return;
    const dateStr = selectedDate.toISOString().split('T')[0];
    const updates: Record<string, number> = {};
    for (const [menuId, pax] of Object.entries(editingPax)) {
      updates[`${menuId}-${dateStr}`] = pax;
    }
    setForecastPax((prev) => ({
      ...prev,
      ...updates 
    }));
    setSelectedDate(null);
  };

  const handlePaxChange = (menuId: string, date: string, value: number) => {
    setForecastPax((prev) => ({
      ...prev,
      [`${menuId}-${date}`]: value 
    }));
  };

  // CRUD handlers
  const handleAddMenu = () => {
    setMenuFormData(INITIAL_FORM_DATA);
    setEditingMenu(null);
    setShowAddModal(true);
  };

  const handleEditMenu = () => {
    if (selectedMenus.length === 1) {
      const menu = menus.find((m) => m.menuId === selectedMenus[0]);
      if (menu) {
        setMenuFormData({
          name: menu.name,
          venueId: menu.venueId,
          type: menu.type,
          description: menu.description ?? '',
          effectiveFrom: menu.effectiveFrom,
          effectiveTo: menu.effectiveTo ?? '',
          defaultPax: menu.defaultPax?.toString() ?? '',
        });
        setEditingMenu(menu.menuId);
        setShowAddModal(true);
      }
    }
  };

  const handleDeleteMenus = () => {
    toast.success(`Deleted ${selectedMenus.length} menu(s)`);
    setSelectedMenus([]);
  };

  const handleSaveMenu = () => {
    if (!menuFormData.name || !menuFormData.venueId || !menuFormData.effectiveFrom) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success(editingMenu ? `Updated ${menuFormData.name}` : `Created ${menuFormData.name}`);
    setShowAddModal(false);
    setSelectedMenus([]);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported 8 menus');
      refetch();
    }, 1500);
  };

  const handleAISuggestion = (suggestion: MenuSuggestion) => {
    // Pre-fill the form with AI suggestion
    setMenuFormData({
      name: suggestion.name,
      venueId: selectedVenueId ?? '',
      type: suggestion.type,
      description: suggestion.description,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
      defaultPax: suggestion.targetPax.toString(),
    });
    setEditingMenu(null);
    setShowAddModal(true);
    toast.success(`AI suggested: ${suggestion.name} with ${suggestion.items.length} items`);
  };

  const exportData = menus.map((m) => ({
    name: m.name,
    venue: m.venueName,
    type: m.type,
    status: m.status,
    effectiveFrom: m.effectiveFrom,
    effectiveTo: m.effectiveTo ?? '',
    totalItems: m.totalItems,
    defaultPax: m.defaultPax ?? '',
  }));

  return (
    <div className="space-y-6">
      {/* Recipe Detail Modal (for viewing recipes from menu) */}
      <RecipeDetailModal
        recipe={viewingRecipe}
        isOpen={viewingRecipe !== null}
        onClose={() => setViewingRecipe(null)}
        allRecipes={availableRecipes}
      />

      {/* AI Menu Suggestion Modal */}
      <MenuSuggestionModal
        isOpen={showAISuggestionModal}
        onClose={() => setShowAISuggestionModal(false)}
        onSuggestionGenerated={handleAISuggestion}
        availableRecipes={availableRecipes}
        venueName={selectedSite?.name}
        menuType={selectedType}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Menus</h1>
          <p className="text-muted-foreground">Manage venue menus, schedules, and demand forecasting</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAISuggestionModal(true)}
            className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-200 hover:border-purple-300"
          >
            <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
            AI Suggest Menu
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Site and Venue Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <select
            value={selectedSiteId}
            onChange={(e) => {
              setSelectedSiteId(e.target.value);
              setSelectedVenueId(undefined);
            }}
            className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {sites.map((site) => (
              <option key={site.siteId} value={site.siteId}>
                {site.name} ({site.code})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
          <select
            value={selectedVenueId ?? ''}
            onChange={(e) => setSelectedVenueId(e.target.value || undefined)}
            className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Venues</option>
            {venues.filter((v) => v.isActive).map((venue) => (
              <option key={venue.venueId} value={venue.venueId}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <select
            value={selectedType ?? ''}
            onChange={(e) => setSelectedType((e.target.value || undefined) as MenuType | undefined)}
            className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {MEAL_TYPES.map((type) => (
              <option key={type.label} value={type.value ?? ''}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {activeTab === 'calendar' && (
        <CalendarView
          menus={menus}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          calendarDays={calendarDays}
          forecastPax={forecastPax}
          selectedDate={selectedDate}
          editingPax={editingPax}
          setEditingPax={setEditingPax}
          openDayModal={openDayModal}
          saveDayForecast={saveDayForecast}
          setSelectedDate={setSelectedDate}
        />
      )}

      {activeTab === 'forecast' && (
        <ForecastView
          menus={menus}
          stats={stats}
          forecastPax={forecastPax}
          handlePaxChange={handlePaxChange}
        />
      )}

      {activeTab === 'overview' && (
        <OverviewView
          menus={menus}
          stats={stats}
          isLoading={isLoading}
          error={error}
          refetch={refetch}
          selectedMenus={selectedMenus}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          handleAddMenu={handleAddMenu}
          handleEditMenu={handleEditMenu}
          handleDeleteMenus={handleDeleteMenus}
          handleBulkUpload={handleBulkUpload}
          exportData={exportData}
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          editingMenu={editingMenu}
          menuFormData={menuFormData}
          setMenuFormData={setMenuFormData}
          handleSaveMenu={handleSaveMenu}
          venues={venues}
          viewingMenu={viewingMenu}
          setViewingMenu={setViewingMenu}
          availableRecipes={availableRecipes}
          onSaveMenuItems={() => {
            toast.success('Menu items updated');
            refetch();
          }}
          onViewRecipe={setViewingRecipe}
        />
      )}
    </div>
  );
}
